import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Archive, ArchiveRestore, FileText, Eye, Edit3, ArrowLeft } from "lucide-react";
import { adminGet, adminPost, isLoggedIn } from "../lib/adminAuth";

function fmt(n, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(n || 0);
}

export default function QuotationList() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await adminGet(`/admin/quotations?include_archived=${includeArchived ? 1 : 0}`);
      setRows(d.rows || []);
    } catch (err) {
      if (err.message === "unauthorized") { nav("/admin/login"); return; }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [includeArchived, nav]);

  useEffect(() => {
    if (!isLoggedIn()) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const createNew = async () => {
    try {
      const doc = await adminPost("/admin/quotations", {
        client: {}, event: {}, services: [], reference_images: [], line_items: [],
        currency: "USD", discount_amount: 0, tax_percent: 5.5, deposit_percent: 15,
        terms: "A non-refundable deposit is required to confirm the booking. Balance due 14 days prior to the event.",
        status: "draft",
      });
      nav(`/admin/quotations/${doc.id}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this quotation? You can restore it later.")) return;
    try { await adminPost(`/admin/quotations/${id}/archive`, {}); load(); }
    catch (err) { setError(err.message); }
  };
  const restore = async (id) => {
    try { await adminPost(`/admin/quotations/${id}/restore`, {}); load(); }
    catch (err) { setError(err.message); }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg-alt))] text-[hsl(var(--ink))]">
      <header className="bg-[hsl(var(--bg))] border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"><ArrowLeft size={18} /></Link>
            <img src="https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/v1a0y2ji_Shobhawithshubhra-logo-final.png" alt="SwS" className="h-10 w-auto object-contain" />
            <div className="font-editorial text-2xl tracking-wider">SwS <span className="text-[hsl(var(--ink-soft))] text-sm tracking-[0.35em] uppercase ml-2">Quotations</span></div>
          </div>
          <button onClick={createNew} className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] px-4 py-2 text-[11px] tracking-[0.3em] uppercase transition-colors">
            <Plus size={14} /> New quotation
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10">
        {error && <div className="border border-[hsl(var(--accent))] text-[hsl(var(--accent))] p-4 text-sm mb-6">{error}</div>}
        <label className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase mb-6 cursor-pointer">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Show archived
        </label>

        <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--line))]">
          {(() => {
            if (loading) {
              return <div className="p-8 text-[hsl(var(--ink-soft))]">Loading…</div>;
            }
            if (rows.length === 0) {
              return (
                <div className="p-8 text-center">
                  <FileText size={32} className="mx-auto mb-3 text-[hsl(var(--ink-soft))]" />
                  <div className="font-display text-2xl">No quotations yet</div>
                  <div className="text-sm text-[hsl(var(--ink-soft))] mt-2">Click <strong>New quotation</strong> to create your first one.</div>
                </div>
              );
            }
            return (
              <table className="w-full text-sm">
              <thead className="text-left kicker border-b border-[hsl(var(--line))]">
                <tr>
                  <th className="p-4">Number</th>
                  <th className="p-4">Client</th>
                  <th className="p-4">Event</th>
                  <th className="p-4">Date</th>
                  <th className="p-4">Total</th>
                  <th className="p-4">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((q) => (
                  <tr key={q.id} className={`border-b border-[hsl(var(--line))] hover:bg-[hsl(var(--bg-alt))] ${q.archived ? "opacity-50" : ""}`}>
                    <td className="p-4 font-display text-base">{q.number}</td>
                    <td className="p-4">{q.client?.name || "—"}</td>
                    <td className="p-4">{q.event?.event_type || "—"}</td>
                    <td className="p-4 text-[hsl(var(--ink-soft))]">{q.event?.date || "—"}</td>
                    <td className="p-4">{fmt(q.totals?.total, q.currency)}</td>
                    <td className="p-4">
                      <span className={`inline-block px-2 py-0.5 text-[10px] tracking-[0.25em] uppercase border ${q.archived ? "border-[hsl(var(--ink-soft))] text-[hsl(var(--ink-soft))]" : "border-[hsl(var(--accent))] text-[hsl(var(--accent))]"}`}>
                        {q.archived ? "Archived" : q.status || "draft"}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3 justify-end">
                        <Link to={`/admin/quotations/${q.id}`} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]" title="Edit"><Edit3 size={14} /></Link>
                        <Link to={`/admin/quotations/${q.id}/print`} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]" title="View / Print" target="_blank"><Eye size={14} /></Link>
                        {q.archived ? (
                          <button onClick={() => restore(q.id)} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]" title="Restore"><ArchiveRestore size={14} /></button>
                        ) : (
                          <button onClick={() => archive(q.id)} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]" title="Archive"><Archive size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            );
          })()}
        </div>
      </main>
    </div>
  );
}
